/**
 * Buggino – global behaviour: header disclosures, mobile menu, localization
 * and sort auto-submit, quantity steppers, dialog helpers and a shared
 * money formatter (window.Buggino).
 */
(function () {
  'use strict';

  const Buggino = (window.Buggino = window.Buggino || {});

  /**
   * Formats an amount in cents using the shop's money format
   * (e.g. "{{amount_with_comma_separator}} €").
   */
  Buggino.formatMoney = function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const fmt = format || '{{amount_with_comma_separator}} €';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = match ? match[1] : 'amount';

    function withDelimiters(num, precision, thousands, decimal) {
      const fixed = (num / 100).toFixed(precision);
      const parts = fixed.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? int + decimal + parts[1] : int;
    }

    let out;
    switch (type) {
      case 'amount_no_decimals':
        out = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = withDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        out = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        out = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        out = withDelimiters(value, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]+>/g, '').trim();
  };

  Buggino.openDialog = function openDialog(dialog, opener) {
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    dialog.__opener = opener || document.activeElement;
    dialog.showModal();
  };

  Buggino.closeDialog = function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
    if (dialog.__opener && typeof dialog.__opener.focus === 'function') {
      dialog.__opener.focus();
    }
  };

  /** Close a dialog when the backdrop is clicked. */
  Buggino.bindBackdropClose = function bindBackdropClose(dialog) {
    if (!dialog || dialog.__backdropBound) return;
    dialog.__backdropBound = true;
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) Buggino.closeDialog(dialog);
    });
  };

  Buggino.announce = function announce(message) {
    let region = document.getElementById('buggino-live');
    if (!region) {
      region = document.createElement('div');
      region.id = 'buggino-live';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  function init() {
    // Header disclosures: close on outside click and Escape.
    const disclosures = Array.from(document.querySelectorAll('details[data-disclosure]'));
    document.addEventListener('click', (event) => {
      disclosures.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.removeAttribute('open');
      });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      disclosures.forEach((details) => {
        if (details.open) {
          details.removeAttribute('open');
          const summary = details.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });
    disclosures.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        const field = details.querySelector('input:not([type="hidden"]), select');
        if (field) field.focus();
      });
    });

    // Mobile menu.
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        const open = mobileMenu.hasAttribute('hidden');
        mobileMenu.toggleAttribute('hidden', !open);
        menuToggle.setAttribute('aria-expanded', String(open));
      });
    }

    // Localization: submit on change.
    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });

    // Collection sort: submit on change.
    document.querySelectorAll('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });

    // Quantity steppers (delegated; works for drawer lines rendered later).
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-qty-change]');
      if (!button) return;
      const wrapper = button.closest('[data-qty]');
      const input = wrapper && wrapper.querySelector('[data-qty-input]');
      if (!input) return;
      const min = Number(input.min || 0);
      const next = Math.max(min, (Number(input.value) || 0) + Number(button.dataset.qtyChange));
      if (next === Number(input.value)) return;
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Cart page: update quantities on change.
    const cartPage = document.querySelector('[data-cart-page]');
    if (cartPage) {
      cartPage.addEventListener('change', (event) => {
        if (!event.target.matches('[data-qty-input]')) return;
        if (typeof cartPage.requestSubmit === 'function') cartPage.requestSubmit();
        else cartPage.submit();
      });
    }

    // Collection filters: collapsed by default on small screens (rendered open for no-JS and desktop).
    const filters = document.querySelector('[data-filters]');
    if (filters && window.matchMedia('(max-width: 899px)').matches) {
      filters.removeAttribute('open');
    }

    // Generic dialog close buttons and backdrop handling.
    document.querySelectorAll('dialog.dialog').forEach((dialog) => Buggino.bindBackdropClose(dialog));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
